import React, { useEffect, useMemo, useRef, useState } from "react";
import { saveFixationAttachment } from "../utils/fixationAttachment";
import { confirmToast, showToast } from "../utils/toast";
import axios from "../utils/axiosConfig";
import JSZip from "jszip";
import { containerAdvance, buildBLInvoices } from "../utils/advanceAllocation";
import { populateContainerWorksheet, populateDealWorksheet, removeCalculationChain } from "../utils/fixationWorkbook";
import { EMPTY_FIXATION_PRICING_DEFAULTS } from "../utils/fixationDefaults";
import "./FixationModal.css";
import { prepareGmail, authorizeGmail, createGmailDraft } from "../utils/gmailDraft";
import { fixationEmailContent } from "../utils/fixationEmail";
import { fixationAllocations, fixationMatchesContainer } from "../utils/fixationAllocation";
import AdminDeleteApprovalDialog from "./AdminDeleteApprovalDialog";

const today = () => new Date().toISOString().split("T")[0];

const normalizeFixationOption = (option) => {
  if (option === "LME_3MONTH") return "LME_3_MONTH";
  if (option === "LME_AVERAGE") return "AVERAGE";
  return option || "";
};

const emptyForm = (currency = "USD", defaults = {}) => ({
  fixationDate: today(),
  shipmentId: defaults.shipmentId ?? "",
  shipmentIds: defaults.shipmentId ? [String(defaults.shipmentId)] : [],
  containerNo: defaults.containerNo || "",
  quantity: "",
  fixationType: normalizeFixationOption(defaults.fixationOption) === "AVERAGE" ? "AVERAGE" : "LME",
  // New entries and resets require fresh market amounts, even when the deal has prices.
  lmePrice: "",
  lme3Month: "",
  cashPrice: "",
  commodity: defaults.commodity || "",
  currency: currency || "USD",
  percentage: defaults.percentage ?? "",
  fixationOption: normalizeFixationOption(defaults.fixationOption),
  fixationValue: "",
  pricePMT: defaults.pricePMT ?? "",
  otherCharges: defaults.otherCharges ?? "",
  chargeType: defaults.chargeType || "",
  finalPrice: "",
  remark: ""
});

const defaultForm = (currency = "USD", defaults = {}) => {
  const form = emptyForm(currency, defaults);
  return calculatePricing(form, "fixationOption");
};

const Required = () => <span className="text-danger ms-1">*</span>;

const DECIMAL_PLACES = 2;
const roundAmount = (value) => Number(Number(value || 0).toFixed(DECIMAL_PLACES));
const calculatedNumber = (value) => roundAmount(value).toFixed(DECIMAL_PLACES);
const toNumber = (value) => Number(value || 0) || 0;
const formatAmount = (value, decimals = 2) => toNumber(value).toFixed(decimals);
const getItemName = (item) => (
  item.ItemName || item.itemName || item.MaterialBuyer || item.MaterialSupplier || item.ItemCode || item.itemCode || "-"
);
const getDealItemId = (item) => item.DealItemID || item.dealItemId || "";
const getItemId = (item) => item.ItemID || item.ItemId || item.itemId || "";
const getItemQuantity = (item) => item.Quantity ?? item.quantity ?? "";
const getItemNetWeight = (item) => item.NetWeight ?? item.netWeight ?? "";
const calculateItemAmount = (item, form) => {
  const lme = roundAmount(form.fixationValue || form.lmePrice || item.LME || form.finalPrice);
  // PriceAdjustmentValue = "Per Of LME" (always a percentage of LME).
  // PriceAdjustmentType = "Disc&Prem" (a flat +/- amount added on top).
  const perOfLme = toNumber(item.PriceAdjustmentValue ?? item.priceAdjustmentValue ?? form.percentage);
  const discPrem = toNumber(item.PriceAdjustmentType ?? item.priceAdjustmentType);
  const quantity = toNumber(getItemQuantity(item));
  const netWeight = toNumber(getItemNetWeight(item));
  const amountQuantity = netWeight || quantity;
  const price = roundAmount((lme * perOfLme / 100) + discPrem);

  return {
    lme,
    lmePercent: perOfLme,
    price,
    quantity,
    netWeight,
    finalAmount: roundAmount(price * amountQuantity)
  };
};

const calculatePricing = (values, changedField) => {
  const next = { ...values };
  const marketFields = ["lmePrice", "lme3Month", "cashPrice", "fixationOption"];

  if (marketFields.includes(changedField)) {
    const threeMonth = next.lme3Month !== "" ? next.lme3Month : next.lmePrice;
    const cash = next.cashPrice !== "" ? next.cashPrice : next.lmePrice;

    if (next.fixationOption === "LME_3_MONTH") {
      next.fixationValue = threeMonth;
    } else if (next.fixationOption === "LME_CASH") {
      next.fixationValue = cash;
    } else if (next.fixationOption === "LME_LOWEST") {
      const availableValues = [threeMonth, cash].filter((value) => value !== "");
      next.fixationValue = availableValues.length
        ? calculatedNumber(Math.min(...availableValues.map(Number)))
        : "";
    } else if (next.fixationOption === "LME_HIGHEST") {
      const availableValues = [threeMonth, cash].filter((value) => value !== "");
      next.fixationValue = availableValues.length
        ? calculatedNumber(Math.max(...availableValues.map(Number)))
        : "";
    } else if (next.fixationOption === "AVERAGE" && next.fixationType === "AVERAGE") {
      const availableValues = [threeMonth, cash].filter((value) => value !== "");
      next.fixationValue = availableValues.length
        ? calculatedNumber(availableValues.reduce((sum, value) => sum + Number(value), 0) / availableValues.length)
        : "";
    } else if (changedField === "fixationOption") {
      next.fixationValue = "";
    }
  }

  const pricingFields = [...marketFields, "fixationValue", "percentage"];
  if (pricingFields.includes(changedField)) {
    next.pricePMT = next.fixationValue !== ""
      ? next.fixationValue
      : "";
  }

  if ([...pricingFields, "pricePMT", "otherCharges"].includes(changedField)) {
    next.finalPrice = next.fixationValue !== ""
      ? next.fixationValue
      : "";
  }

  return next;
};

const presentValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");
const dealDefaultsCache = new Map();
const fixationRowsCache = new Map();

const FixationModal = ({
  show,
  dealId,
  totalQty,
  dealCurrency = "USD",
  dealPricingDefaults = EMPTY_FIXATION_PRICING_DEFAULTS,
  shipmentWorksheetRows = [],
  onClose
}) => {
  const [fixations, setFixations] = useState([]);
  const [dealDefaults, setDealDefaults] = useState({
    totalQty: "",
    containerCount: "",
    currency: dealCurrency,
    commodities: [],
    commodity: "",
    lmePrice: "",
    lme3Month: "",
    cashPrice: "",
    percentage: "",
    fixationOption: ""
  });
  const [form, setForm] = useState(defaultForm(dealCurrency));
  const [dealItems, setDealItems] = useState([]);
  const [dealContainers, setDealContainers] = useState([]);
  const [fixationContainers, setFixationContainers] = useState([]);
  const [itemMasterCommodities, setItemMasterCommodities] = useState([]);
  const [editId, setEditId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState("");
  const [exportError, setExportError] = useState("");
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [fetchedShipmentRows, setFetchedShipmentRows] = useState([]);
  useEffect(() => {
    if (show) prepareGmail().catch(() => {});
  }, [show]);
  const containerDropdownRef = useRef(null);
  useEffect(() => {
    if (!show) return;
    const closeOutside = (event) => {
      const dropdown = containerDropdownRef.current;
      if (dropdown && !dropdown.contains(event.target)) dropdown.open = false;
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && containerDropdownRef.current) containerDropdownRef.current.open = false;
    };
    document.addEventListener("click", closeOutside);
    document.addEventListener("focusin", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("click", closeOutside);
      document.removeEventListener("focusin", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [show]);
  const requestIdRef = useRef(0);
  const loadedKeyRef = useRef("");
  const inFlightKeyRef = useRef("");

  const normalizedPricingDefaults = useMemo(() => ({
    lmePrice: dealPricingDefaults?.lmePrice ?? "",
    lme3Month: dealPricingDefaults?.lme3Month ?? "",
    cashPrice: dealPricingDefaults?.cashPrice ?? "",
    percentage: dealPricingDefaults?.percentage ?? "",
    fixationOption: dealPricingDefaults?.fixationOption ?? ""
  }), [
    dealPricingDefaults?.lmePrice,
    dealPricingDefaults?.lme3Month,
    dealPricingDefaults?.cashPrice,
    dealPricingDefaults?.percentage,
    dealPricingDefaults?.fixationOption
  ]);
  const pricingDefaultsKey = useMemo(
    () => JSON.stringify(normalizedPricingDefaults),
    [normalizedPricingDefaults]
  );

  const effectiveCurrency = dealCurrency || dealDefaults.currency || "USD";
  const effectiveTotalQty = totalQty !== "" && totalQty !== null && totalQty !== undefined
    ? totalQty
    : dealDefaults.totalQty;

  const fixedQty = useMemo(
    () => fixations.reduce((sum, row) => sum + Number(row.Quantity || 0), 0),
    [fixations]
  );
  const balanceQty = Math.max(Number(effectiveTotalQty || 0) - fixedQty, 0);

  useEffect(() => {
    if (!show || !dealId) {
      inFlightKeyRef.current = "";
      setFetchedShipmentRows([]);
      return;
    }

    const loadKey = `${dealId}|${dealCurrency || "USD"}|${pricingDefaultsKey}`;
    if (inFlightKeyRef.current === loadKey) return;

    const cachedDefaults = dealDefaultsCache.get(loadKey);
    const cachedFixations = fixationRowsCache.get(String(dealId));
    setEditId(null);
    setSelectedIds([]);
    setFixationContainers([]);

    if (cachedDefaults) {
      setDealDefaults(cachedDefaults.defaults);
      setDealItems(cachedDefaults.items);
      setDealContainers(cachedDefaults.containers);
      setItemMasterCommodities([]);
      setForm(defaultForm(dealCurrency || cachedDefaults.defaults.currency, cachedDefaults.defaults));
    } else {
      setForm(defaultForm(dealCurrency, normalizedPricingDefaults));
    }

    if (cachedFixations) setFixations(cachedFixations);

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    inFlightKeyRef.current = loadKey;
    setLoadingDefaults(true);

    const loadModalData = async () => {
      try {
        const [fixationResult, dealResult, containerResult] = await Promise.allSettled([
          axios.get(`/newdeal/${dealId}/fixations`),
          axios.get(`/newdeal/${dealId}`),
          axios.get(`/newdeal/${dealId}/fixation-containers`)
        ]);

        if (requestIdRef.current !== requestId) return;

        const loadedFixations = fixationResult.status === "fulfilled"
          ? (Array.isArray(fixationResult.value.data) ? fixationResult.value.data : [])
          : (cachedFixations || []);

        if (containerResult.status === "fulfilled") {
          setFixationContainers(Array.isArray(containerResult.value.data) ? containerResult.value.data : []);
        } else {
          console.error("Error loading fixation containers:", containerResult.reason);
        }

        if (fixationResult.status === "fulfilled") {
          const rows = loadedFixations;
          fixationRowsCache.set(String(dealId), rows);
          setFixations(rows);
          setSelectedIds((current) => current.filter((fixationId) =>
            rows.some((row) => row.FixationID === fixationId)
          ));
        } else {
          console.error("Error loading fixations:", fixationResult.reason);
        }

        const shipmentResult = await axios.get(`/shipment/${dealId}`).catch(() => null);
        if (requestIdRef.current !== requestId) return;
        if (shipmentResult) {
          const { shipment = [], itemDetails = [] } = shipmentResult.data || {};
          const itemsByShipment = {};
          itemDetails.forEach((item) => {
            const sid = String(item.ShipmentID || "");
            if (!itemsByShipment[sid]) itemsByShipment[sid] = [];
            itemsByShipment[sid].push(item);
          });
          const rows = shipment
            .map((s) => ({
              ShipmentID: s.ShipmentID,
              containerNo: s.ContainerNo || "",
              netWeight: s.NetWeight ?? "",
              advancePayment: s.AdvancePayment ?? 0,
              shipLine: s.ShipLine || "",
              vesselName: s.VesselName || "",
              invoiceNo: s.InvoiceNo || "",
              invoiceDate: s.InvoiceDate || "",
              blNo: s.BLNo || "",
              itemDetails: (itemsByShipment[String(s.ShipmentID || "")] || []).map((item) => ({
                dealItemId: item.DealItemID,
                itemId: item.ItemID,
                itemName: item.ItemName || "",
                itemCode: item.ItemCode || "",
                quantity: item.Quantity,
                netWeight: item.NetWeight ?? ""
              }))
            }))
            .filter((row) => row.itemDetails.length > 0);
          setFetchedShipmentRows(rows);
        }

        if (dealResult.status === "fulfilled") {
          const {
            deal = {},
            items = [],
            containers = []
          } = dealResult.value.data || {};
          const dealCommodities = [...new Set(items
            .map((item) => item.Commodity || item.ItemName || item.MaterialSupplier || item.MaterialBuyer)
            .filter(Boolean))];
          const defaults = {
            deal,
            totalQty: deal.TotalQty ?? "",
            containerCount: deal.NoOfContainer ?? "",
            currency: deal.DealCurrency || dealCurrency || "USD",
            commodities: dealCommodities,
            commodity: dealCommodities[0] || "",
            lmePrice: presentValue(normalizedPricingDefaults.lmePrice, items[0]?.LME, ""),
            lme3Month: presentValue(normalizedPricingDefaults.lme3Month, items[0]?.LME, ""),
            cashPrice: presentValue(normalizedPricingDefaults.cashPrice, items[0]?.LME, ""),
            percentage: presentValue(
              items.find(item => String(item.PriceMode).toLowerCase() === "unfixed")?.PriceAdjustmentValue,
              normalizedPricingDefaults.percentage,
              deal.IsPercent ? 100 - Number(deal.UnFixedAmount) : "",
              ""
            ),
            otherCharges: items.find(item => String(item.PriceMode).toLowerCase() === "unfixed")?.PriceAdjustmentType ?? "",
            fixationOption: presentValue(
              normalizedPricingDefaults.fixationOption,
              items.find((item) => item.LMEOption)?.LMEOption,
              deal.LMEOption,
              ""
            )
          };

          dealDefaultsCache.set(loadKey, { defaults, items, containers });
          setDealItems(items);
          setDealContainers(containers);
          setItemMasterCommodities([]);
          setDealDefaults(defaults);
          setForm(defaultForm(dealCurrency || defaults.currency, defaults));
        } else {
          console.error("Error loading fixation deal defaults:", dealResult.reason);
        }

        loadedKeyRef.current = loadKey;
      } finally {
        if (requestIdRef.current === requestId) {
          inFlightKeyRef.current = "";
          setLoadingDefaults(false);
        }
      }
    };

    loadModalData();

    return () => {
      requestIdRef.current += 1;
    };
  }, [show, dealId, dealCurrency, pricingDefaultsKey, normalizedPricingDefaults]);

  const setValue = (name, value) => {
    setForm((prev) => calculatePricing({ ...prev, [name]: value }, name));
  };

  const setFixationType = (fixationType) => {
    setForm((prev) => {
      const next = {
        ...prev,
        fixationType,
        fixationOption: fixationType === "AVERAGE" ? "AVERAGE"
          : prev.fixationOption === "AVERAGE" ? "" : prev.fixationOption
      };
      return calculatePricing(next, "fixationOption");
    });
  };

  const resetForm = () => {
    setEditId(null);
    setSelectedIds([]);
    setForm(defaultForm(effectiveCurrency, dealDefaults));
  };

  const refreshFixations = async () => {
    if (!dealId) return;
    try {
      const res = await axios.get(`/newdeal/${dealId}/fixations`);
      const rows = Array.isArray(res.data) ? res.data : [];
      fixationRowsCache.set(String(dealId), rows);
      setFixations(rows);
      setSelectedIds((current) => current.filter((fixationId) =>
        rows.some((row) => row.FixationID === fixationId)
      ));
      return rows;
    } catch (err) {
      console.error("Error loading fixations:", err);
      return null;
    }
  };

  const saveFixation = async () => {
    const calculatedForm = calculatePricing(form, "fixationValue");
    const submissionForm = {
      ...calculatedForm,
      // LMEPrice is retained for compatibility with reports and existing APIs,
      // but its value is now the result of the selected fixation rule.
      lmePrice: calculatedForm.fixationValue === ""
        ? null
        : toNumber(calculatedForm.fixationValue)
    };
    if (!submissionForm.fixationDate || Number(submissionForm.quantity) <= 0 || !submissionForm.quantity || !submissionForm.fixationType || submissionForm.finalPrice === "") {
      alert("Fixation date, quantity, type and fixation value are required");
      return;
    }
    if (!submissionForm.shipmentIds.length) {
      alert("Select at least one container before saving the fixation.");
      return;
    }
    const editedQty = editId
      ? Number(fixations.find((row) => row.FixationID === editId)?.Quantity || 0)
      : 0;
    if (Number(form.quantity) > balanceQty + editedQty) {
      alert("Fixation quantity cannot exceed balance quantity");
      return;
    }

    try {
      setSaving(true);
      const response = editId
        ? await axios.put(`/newdeal/${dealId}/fixations/${editId}`, submissionForm)
        : await axios.post(`/newdeal/${dealId}/fixations`, submissionForm);
      const rows = await refreshFixations();
      const savedId = response.data?.FixationID || editId;
      const savedRow = rows?.find(row => String(row.FixationID) === String(savedId));
      const savedContainers = savedRow ? fixationAllocations(savedRow) : [];
      if (!savedRow || savedContainers.length !== submissionForm.shipmentIds.length ||
          !submissionForm.shipmentIds.every(id => savedContainers.some(container => String(container.ShipmentID) === String(id)))) {
        // Keep the input for recovery; never invent persisted assignments in the grid.
        if (savedId) setEditId(savedId);
        setSelectedIds([]);
        alert("The saved container assignments could not be verified. Your selections have been kept. Ensure the updated backend is deployed and restarted, then edit/update this fixation. Email is unavailable until the saved containers are shown.");
        return;
      }
      setEditId(null);
      setSelectedIds([savedRow.FixationID]);
      setForm(defaultForm(effectiveCurrency, dealDefaults));
    } catch (err) {
      const responseData = err.response?.data;
      const message = responseData?.message || (typeof responseData === "string" ? responseData : err.message);
      alert(message || "Error saving fixation");
    } finally {
      setSaving(false);
    }
  };

  const editFixation = (row) => {
    setEditId(row.FixationID);
    setForm({
      fixationDate: row.FixationDate?.split("T")[0] || today(),
      shipmentId: row.ShipmentID ?? "",
      shipmentIds: fixationAllocations(row).map(container => String(container.ShipmentID)),
      containerNo: row.ContainerNo || "",
      quantity: row.Quantity ?? "",
      fixationType: row.FixationType || "LME",
      lmePrice: row.LMEPrice ?? "",
      lme3Month: row.LME3Month ?? "",
      cashPrice: row.CashPrice ?? "",
      commodity: row.Commodity || dealDefaults.commodity || "",
      currency: row.Currency || effectiveCurrency,
      percentage: row.Percentage ?? "",
      fixationOption: normalizeFixationOption(row.FixationOption),
      fixationValue: row.FixationValue ?? "",
      pricePMT: row.PricePMT ?? "",
      otherCharges: row.OtherCharges ?? "",
      chargeType: row.ChargeType || "",
      finalPrice: row.FinalPrice ?? "",
      remark: row.Remark || ""
    });
  };

  const deleteSelectedFixations = async () => {
    if (!selectedIds.length) return;
    const label = selectedIds.length === 1 ? "this fixation" : `${selectedIds.length} selected fixations`;
    if (!await confirmToast(`Are you sure you want to delete ${label}?`)) return;
    setPendingDeleteIds([...selectedIds]);
  };

  const deleteSelectedWithAdminApproval = async (approvalToken) => {
    const results = await Promise.allSettled(pendingDeleteIds.map((fixationId) =>
        axios.delete(`/newdeal/${dealId}/fixations/${fixationId}`, {
          headers: { "X-Fixation-Delete-Approval": approvalToken }
        })
      ));
    const firstFailure = results.find((result) => result.status === "rejected");
    if (pendingDeleteIds.includes(editId)) resetForm();
    setSelectedIds([]);
    setPendingDeleteIds([]);
    await refreshFixations();
    if (firstFailure) {
      showToast(firstFailure.reason?.response?.data?.message || "One or more selected fixations could not be deleted");
    }
  };

  const toggleSelected = (fixationId) => {
    setSelectedIds((current) => current.includes(fixationId)
      ? current.filter((id) => id !== fixationId)
      : [...current, fixationId]);
  };

  const toggleContainer = (shipmentId) => {
    const id = String(shipmentId);
    setForm(prev => ({ ...prev, shipmentId: "", containerNo: "",
      shipmentIds: prev.shipmentIds.includes(id) ? prev.shipmentIds.filter(value => value !== id) : [...prev.shipmentIds, id]
    }));
  };

  const availableContainers = fixationContainers.map((container, index) => ({ ...container, displayIndex: index + 1 }))
    .filter(container => !fixations.some(row =>
      String(row.FixationID) !== String(editId) && fixationMatchesContainer(row, container)
    ));

  const selectedFixation = selectedIds.length === 1
    ? fixations.find(row => row.FixationID === selectedIds[0]) : null;
  const exportDisabled = Boolean(exporting) || !fixationAllocations(selectedFixation).length;

  if (!show) return null;

  const numberInput = (name, className = "", integerOnly = false) => (
    <input
      type="number"
      step={integerOnly ? "1" : name === "quantity" ? "0.001" : "0.01"}
      min={integerOnly ? "0" : undefined}
      inputMode={integerOnly ? "numeric" : "decimal"}
      className={`form-control form-control-sm ${className}`.trim()}
      value={integerOnly && form[name] !== "" ? Math.trunc(toNumber(form[name])) : form[name]}
      onChange={(e) => setValue(
        name,
        integerOnly ? e.target.value.replace(/\D/g, "") : e.target.value
      )}
    />
  );

  const commodityOptions = dealDefaults.commodities.length
    ? dealDefaults.commodities
    : itemMasterCommodities.length
      ? itemMasterCommodities
    : ["Aluminium", "Copper", "Lead", "Nickel", "Zinc"];
  const containerSections = dealContainers.length
    ? dealContainers
    : Array.from({ length: toNumber(dealDefaults.containerCount) }, (_, index) => ({
      ContainerID: `planned-${index}`
    }));
  const findDealItem = (shipmentItem) => dealItems.find((item) => (
    String(getDealItemId(item) || "") === String(getDealItemId(shipmentItem) || "") ||
    String(getItemId(item) || "") === String(getItemId(shipmentItem) || "") ||
    (getItemName(item) !== "-" && getItemName(item) === getItemName(shipmentItem))
  ));
  const hasShipmentWorksheetRows = Array.isArray(shipmentWorksheetRows) && shipmentWorksheetRows.some((row) =>
    Array.isArray(row?.itemDetails) && row.itemDetails.some((item) => getItemName(item) !== "-" || getItemNetWeight(item) !== "")
  );
  const effectiveShipmentRows = hasShipmentWorksheetRows ? shipmentWorksheetRows : fetchedShipmentRows;
  const hasEffectiveShipmentRows = effectiveShipmentRows.some((row) =>
    Array.isArray(row?.itemDetails) && row.itemDetails.some((item) => getItemName(item) !== "-" || getItemNetWeight(item) !== "")
  );
  const allWorksheetContainers = hasEffectiveShipmentRows
    ? effectiveShipmentRows
      .filter((row) => Array.isArray(row?.itemDetails) && row.itemDetails.length)
      .map((shipment, index) => ({
        ContainerID: shipment.ShipmentID || shipment.containerNo || `shipment-${index}`,
        ContainerNo: shipment.containerNo,
        advancePayment: containerAdvance(shipment),
        ShipLine: shipment.shipLine,
        VesselName: shipment.vesselName,
        InvoiceNo: shipment.invoiceNo,
        InvoiceDate: shipment.invoiceDate,
        BLNo: shipment.blNo,
        items: shipment.itemDetails.map((shipmentItem) => {
          const dealItem = findDealItem(shipmentItem) || {};
          return {
            ...dealItem,
            ...shipmentItem,
            ItemName: shipmentItem.itemName || dealItem.ItemName,
            ItemCode: shipmentItem.itemCode || dealItem.ItemCode,
            Quantity: shipmentItem.quantity ?? dealItem.Quantity,
            NetWeight: shipmentItem.netWeight ?? shipmentItem.NetWeight ?? ""
          };
        })
      }))
    : (containerSections.length ? containerSections : [{ ContainerID: "items" }])
      .map((container) => ({ ...container, items: dealItems }));
  const worksheetContainers = allWorksheetContainers;
  const pricingFormFromRow = (row) => ({
    ...defaultForm(effectiveCurrency, dealDefaults),
    lmePrice: row.LMEPrice ?? "",
    lme3Month: row.LME3Month ?? "",
    cashPrice: row.CashPrice ?? "",
    percentage: row.Percentage ?? "",
    fixationOption: normalizeFixationOption(row.FixationOption),
    fixationValue: row.FixationValue ?? "",
    pricePMT: row.PricePMT ?? "",
    otherCharges: row.OtherCharges ?? "",
    finalPrice: row.FinalPrice ?? ""
  });
  const pricingForContainer = (container) => {
    const containerId = String(container.ContainerID || "");
    if (form.shipmentIds.includes(containerId)) return form;

    const containerFixation = fixations.find((row) =>
      row.FixationID !== editId && fixationMatchesContainer(row, container)
    );
    if (containerFixation) return pricingFormFromRow(containerFixation);
    return null;
  };
  const fixationInvoices = buildBLInvoices(worksheetContainers.map(container => ({
    ...container,
    advancePayment: pricingForContainer(container) ? containerAdvance(container) : 0,
    invoiceAmount: pricingForContainer(container)
      ? container.items.reduce((sum, item) => sum + calculateItemAmount(item, pricingForContainer(container)).finalAmount, 0)
      : 0
  })));
  const worksheetTotal = roundAmount(fixationInvoices.reduce((sum, group) => sum + group.balanceAmount, 0));
  const hasAnyEffectiveContainer = worksheetContainers.some((c) => c.items.length > 0);
  const emailFixationWorkbook = async (fixation, format = "xlsx", composeEmail = false, attachmentOnly = false) => {
    const allocations = fixationAllocations(fixation);
    const emailContainerRows = allocations.map(container => ({
      containerNo: container.ContainerNo || `Container ${container.ShipmentID}`,
      netWeight: Number(container.Quantity)
    }));
    const emailContainers = worksheetContainers.filter(container => fixationMatchesContainer(fixation, container));
    if (!emailContainerRows.length) {
      alert("Select containers and save this fixation before generating its email workbook.");
      return;
    }

    setExporting(format);
    setExportError("");
    try {
      const gmailToken = composeEmail ? await authorizeGmail() : null;
      const materialRows = emailContainers.flatMap((container) => {
        const pricing = pricingFormFromRow(fixation);
        return container.items.map((item) => {
          const amounts = pricing ? calculateItemAmount(item, pricing) : null;
          return {
            name: getItemName(item),
            commodity: item.Commodity || "",
            lme: amounts ? amounts.lme : (item.LME ?? ""),
            percentage: item.PriceAdjustmentValue ?? item.priceAdjustmentValue ?? "",
            adjustment: item.PriceAdjustmentType ?? item.priceAdjustmentType ?? "",
            price: amounts ? amounts.price : (item.Rate ?? "")
          };
        });
      });
      const uniqueMaterials = [...new Map(materialRows.map(row => [JSON.stringify(row), row])).values()];
      let workbookBlob;
      if (format === "pdf") {
        const response = await axios.post(`/newdeal/${dealId}/fixations/${fixation.FixationID}/pdf`, {
          deal: dealDefaults.deal || { DealID: dealId }, materials: uniqueMaterials
        }, { responseType: "blob" });
        workbookBlob = new Blob([response.data], { type: "application/pdf" });
      } else {
        const templateUrl = `${process.env.PUBLIC_URL || ""}/templates/Global_Metals_Metal_Price_Fixation_Template.xlsx`;
        const response = await fetch(templateUrl);
        if (!response.ok) throw new Error("Fixation Excel template could not be loaded.");

        const workbookZip = await JSZip.loadAsync(await response.arrayBuffer());
        const worksheetFile = workbookZip.file("xl/worksheets/sheet1.xml");
        if (!worksheetFile) throw new Error("Fixation Notice sheet is missing from the template.");
        const worksheetXml = populateContainerWorksheet(await worksheetFile.async("string"), emailContainerRows);
        const populated = populateDealWorksheet(worksheetXml, {
          deal: dealDefaults.deal || { DealID: dealId },
          fixations: [fixation],
          materials: uniqueMaterials,
          noticeDate: fixation.FixationDate?.split("T")[0] || today()
        });
        workbookZip.file("xl/worksheets/sheet1.xml", populated.xml);
        if (populated.extraRows) {
          const workbook = workbookZip.file("xl/workbook.xml");
          workbookZip.file("xl/workbook.xml", (await workbook.async("string")).replace("$F$48", `$F$${48 + populated.extraRows}`));
        }
        await removeCalculationChain(workbookZip);

        workbookBlob = await workbookZip.generateAsync({ type: "blob", compression: "DEFLATE" });
      }
      const safeDealId = String(dealId || "deal").replace(/[^a-z0-9_-]/gi, "_");
      const filename = `Metal_Price_Fixation_${safeDealId}_${fixation.FixationID}_${fixation.FixationDate?.split("T")[0]}.${format}`;
      try {
        await saveFixationAttachment(dealId, fixation.FixationID, workbookBlob, filename);
      } catch (error) {
        throw new Error("Fixation Note was generated but could not be saved to Attachments. Please try again.");
      }
      showToast("Fixation Note saved to Attachments successfully.");
      if (attachmentOnly) return;
      if (composeEmail) {
        const draftUrl = await createGmailDraft(gmailToken, {
          ...fixationEmailContent(fixation, format), blob: workbookBlob, filename, format
        });
        window.location.assign(draftUrl);
      } else {
        const downloadUrl = URL.createObjectURL(workbookBlob);
        const downloadLink = document.createElement("a");
        downloadLink.href = downloadUrl;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
        URL.revokeObjectURL(downloadUrl);
      }
    } catch (err) {
      let message = err.response?.data?.message || err.message || "Unable to generate the fixation document.";
      if (err.response?.data instanceof Blob) {
        try { message = JSON.parse(await err.response.data.text()).message || message; } catch { /* Keep the request error. */ }
      }
      setExportError(message);
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="modal show d-block fixation-modal" style={{ background: "rgba(0,0,0,0.62)" }}>
      <div className="modal-dialog modal-dialog-centered fixation-modal-dialog">
        <div className="modal-content">
          <div className="modal-header fixation-modal-header">
            <h5 className="modal-title">Fixation Details :-</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
          </div>

          <div className="modal-body fixation-modal-body">
            <section className="fixation-form-pane">
              <div className="fixation-section-title">Fixation Detail</div>
              <div className="fixation-form-content">
                <div className="fixation-field-row">
                  <label>Fixation Date <Required /></label>
                  <input type="date" className="form-control form-control-sm fixation-date" value={form.fixationDate} onChange={(e) => setValue("fixationDate", e.target.value)} />
                </div>

                <div className="fixation-field-row">
                  <label>Container</label>
                  <details ref={containerDropdownRef} className="fixation-container-select">
                    <summary className="form-select form-select-sm">{form.shipmentIds.length ? `${form.shipmentIds.length} container(s) selected` : "Select containers"}</summary>
                    <div className="fixation-container-options">
                      {availableContainers.map((container) => (
                        <label key={container.ShipmentID} className="fixation-container-option">
                          <input type="checkbox" checked={form.shipmentIds.includes(String(container.ShipmentID))} onChange={() => toggleContainer(container.ShipmentID)} />
                          {`Container-${container.displayIndex} - ${container.ContainerNo || "No number"}${container.ShipLine ? ` | ${container.ShipLine}` : ""}`}
                        </label>
                      ))}
                      {!availableContainers.length && <span>No unfixed containers available</span>}
                    </div>
                  </details>
                </div>

                <div className="fixation-field-row">
                  <label>Quantity <Required /></label>
                  {numberInput("quantity", "fixation-short-input")}
                  {form.shipmentIds.length > 0 && <small>{formatAmount(Number(form.quantity || 0) / form.shipmentIds.length, 3)} MT per container (rounded; total preserved)</small>}
                </div>

                <div className="fixation-field-row fixation-market-row">
                  <span className="fixation-label-spacer"></span>
                  <div className="fixation-market-controls">
                    <label className="fixation-radio">
                      <input type="radio" checked={form.fixationType === "LME"} onChange={() => setFixationType("LME")} />
                      <span>LME</span>
                    </label>
                    <select className="form-select form-select-sm fixation-commodity" aria-label="Commodity" value={form.commodity} onChange={(e) => setValue("commodity", e.target.value)}>
                      <option value="">Select Option</option>
                      {commodityOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                    {numberInput("lme3Month", "fixation-market-input", true)}
                    <span className="text-nowrap">3 Month <Required /></span>
                    {numberInput("cashPrice", "fixation-market-input", true)}
                    <span className="text-nowrap">Cash <Required /></span>
                  </div>
                </div>

                <div className="fixation-field-row fixation-average-row">
                  <span className="fixation-label-spacer"></span>
                  <label className="fixation-radio">
                    <input type="radio" checked={form.fixationType === "AVERAGE"} onChange={() => setFixationType("AVERAGE")} />
                    <span>Average of</span>
                  </label>
                </div>

                <div className="fixation-field-row fixation-combined-row">
                  <label>Fixation</label>
                  <div className="fixation-combined-controls">
                    <span className="text-nowrap">LME 3 Month</span>
                    <span>Of</span>
                    <select className="form-select form-select-sm fixation-option" value={form.fixationOption} onChange={(e) => setValue("fixationOption", e.target.value)}>
                      <option value="">Select Option</option>
                      <option value="LME_3_MONTH">LME 3 Month</option>
                      <option value="LME_CASH">LME Cash</option>
                      <option value="LME_LOWEST">LME Lowest</option>
                      <option value="LME_HIGHEST">LME Highest</option>
                      <option value="AVERAGE" disabled={form.fixationType !== "AVERAGE"}>Average</option>
                    </select>
                  </div>
                </div>

                <div className="fixation-field-row fixation-combined-row fixation-combined-subrow">
                  <span className="fixation-label-spacer"></span>
                  <div className="fixation-combined-controls">
                    <select className="form-select form-select-sm fixation-currency" value={form.currency} onChange={(e) => setValue("currency", e.target.value)}>
                      {[effectiveCurrency, "USD", "INR"].filter((value, index, values) => value && values.indexOf(value) === index).map((currency) => <option key={currency}>{currency}</option>)}
                    </select>
                    {numberInput("fixationValue", "fixation-value-input")}
                  </div>
                </div>

                <div className="fixation-field-row fixation-remark-row">
                  <label>Remark</label>
                  <textarea className="form-control form-control-sm" rows="3" value={form.remark} onChange={(e) => setValue("remark", e.target.value)} />
                </div>

                <div className="fixation-actions">
                  <button type="button" className="btn btn-success btn-sm" onClick={saveFixation} disabled={saving || loadingDefaults}>{editId ? "Update" : "Save"}</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={resetForm}>Clear</button>
                </div>
              </div>
            </section>

            <section className="fixation-list-pane">
              <div className="fixation-totals">
                <div><strong>Total Qty</strong><span>:</span><b>{formatAmount(effectiveTotalQty, 3)} MT</b></div>
                <div><strong>Fixed</strong><span>:</span><b>{formatAmount(fixedQty, 3)} MT</b></div>
                <div><strong>Balance to be Fixed</strong><span>:</span><b>{formatAmount(balanceQty, 3)} MT</b></div>
              </div>

              <div className="fixation-table-wrap">
                <table className="table table-sm table-bordered fixation-table mb-0">
                  <thead>
                    <tr><th></th><th>Fixation ID</th><th>Date</th><th>Container</th><th>Quantity</th><th>Type</th><th>LME</th><th>Final Price</th><th>Action</th><th>Fixation Note</th></tr>
                  </thead>
                  <tbody>
                    {fixations.length === 0 ? (
                      <tr><td colSpan="10" className="text-center text-muted py-3">No fixation has been added so far.</td></tr>
                    ) : fixations.map((row) => {
                      const allocations = fixationAllocations(row);
                      const containerRows = allocations.length ? allocations : [null];
                      const span = containerRows.length;
                      return containerRows.map((container, index) => (
                        <tr key={`${row.FixationID}-${container?.ShipmentID || "missing"}`} className={selectedIds.includes(row.FixationID) ? "table-active" : ""}>
                          {index === 0 && <>
                            <td rowSpan={span}><input type="checkbox" aria-label={`Select fixation ${row.FixationID}`} checked={selectedIds.includes(row.FixationID)} onChange={() => toggleSelected(row.FixationID)} /></td>
                            <td rowSpan={span}>{row.FixationID}</td>
                            <td rowSpan={span}>{row.FixationDate?.split("T")[0] || "-"}</td>
                          </>}
                          <td>{container ? container.ContainerNo || `Container ${container.ShipmentID}` : "Containers missing ? edit to select"}</td>
                          <td>{formatAmount(container ? container.Quantity : row.Quantity, 3)}</td>
                          {index === 0 && <>
                            <td rowSpan={span}>{row.FixationType || "-"}</td>
                            <td rowSpan={span}>{formatAmount(row.FixationValue ?? row.LMEPrice)}</td>
                            <td rowSpan={span}>{row.FinalPrice != null ? formatAmount(row.FinalPrice) : "-"}</td>
                            <td rowSpan={span}><button type="button" className="btn btn-link btn-sm p-0" onClick={() => editFixation(row)}>Edit</button></td>
                            <td rowSpan={span}><button type="button" className="btn btn-link btn-sm p-0" onClick={() => alert(row.Remark || "No fixation note")}>View</button></td>
                          </>}
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>

              <div className="fixation-list-actions">
                <button type="button" className="btn btn-success btn-sm" onClick={deleteSelectedFixations} disabled={!selectedIds.length}>Delete</button>
                <button type="button" className="btn btn-primary btn-sm" disabled={exportDisabled} onClick={() => emailFixationWorkbook(selectedFixation, "pdf")}>{exporting === "pdf" ? "Generating PDF..." : "Download PDF"}</button>
                <button type="button" className="btn btn-outline-success btn-sm" disabled={exportDisabled} onClick={() => emailFixationWorkbook(selectedFixation, "xlsx")}>{exporting === "xlsx" ? "Generating Excel..." : "Download Excel"}</button>
                <button type="button" className="btn btn-link btn-sm" disabled={exportDisabled} onClick={() => emailFixationWorkbook(selectedFixation, "pdf", true)}>Email PDF</button>
                <button type="button" className="btn btn-link btn-sm" disabled={exportDisabled} onClick={() => emailFixationWorkbook(selectedFixation, "xlsx", true)}>Email Excel</button>
              </div>
              {exportError && <div className="alert alert-danger mt-2" role="alert">{exportError}</div>}
              <div className="fixation-delete-note">Only unused fixations can be deleted. Administrator approval and Delete permission are required.</div>
              <button type="button" className="btn btn-outline-primary btn-sm mt-2" disabled={exportDisabled}
                onClick={() => emailFixationWorkbook(selectedFixation, "pdf", false, true)}>Save Note to Attachments</button>

              <section className="fixation-worksheet-pane">
                <div className="fixation-section-title">Container Item List</div>
                <div className="fixation-worksheet-content">
                  {!hasAnyEffectiveContainer ? (
                    <div className="fixation-empty-note">No deal items have been added.</div>
                  ) : worksheetContainers.map((container, containerIndex) => {
                    const containerPricing = pricingForContainer(container);
                    const isContainerFixed = Boolean(containerPricing);
                    const grossContainerTotal = isContainerFixed
                      ? container.items.reduce((sum, item) =>
                        sum + calculateItemAmount(item, containerPricing).finalAmount, 0)
                      : 0;
                    const containerTotal = roundAmount(grossContainerTotal);

                    return (
                      <div className="fixation-container-block" key={container.ContainerID || containerIndex}>
                        <div className="fixation-container-title">
                          <span>Container-{containerIndex + 1}</span>
                          <small>
                            {[container.ContainerNo, container.Size, container.PortLoading, container.PortDischarge, container.DeliveryPort, container.ShipLine, container.BLNo]
                              .filter(Boolean)
                              .join(" | ")}
                          </small>
                        </div>

                        <div className="fixation-table-wrap">
                          <table className="table table-sm table-bordered fixation-table fixation-worksheet-table mb-0">
                            <thead>
                              <tr>
                                <th>LME</th>
                                <th>LME%</th>
                                <th>Price</th>
                                <th>Net Weight</th>
                                <th>Final Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {container.items.map((item) => {
                                const row = isContainerFixed ? calculateItemAmount(item, containerPricing) : null;
                                const netWeight = getItemNetWeight(item);
                                return (
                                  <tr key={`${getDealItemId(item) || getItemId(item) || getItemName(item)}-${getItemNetWeight(item)}`}>
                                    <td className="text-end">{formatAmount(row ? row.lme : 0)}</td>
                                    <td className="text-end">{formatAmount(row ? row.lmePercent : 0)}</td>
                                    <td className="text-end">{formatAmount(row ? row.price : 0)}</td>
                                    <td className="text-end">{netWeight !== "" ? netWeight : 0}</td>
                                    <td className="text-end">{formatAmount(row ? row.finalAmount : 0, 2)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan="4" className="text-end fw-semibold">Container Total (before BL advance)</td>
                                <td className="text-end fw-semibold">{formatAmount(isContainerFixed ? containerTotal : 0, 2)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    );
                  })}

                  {hasAnyEffectiveContainer && (
                    <div className="fixation-table-wrap mt-3">
                      <table className="table table-sm table-bordered fixation-table">
                        <thead><tr><th>BL / Vessel</th><th>Invoice Total</th><th>Advance</th><th>Balance</th></tr></thead>
                        <tbody>{fixationInvoices.map(group => (
                          <tr key={group.key}>
                            <td>{group.blNo || "No BL"} / {group.vesselName || "No vessel"}</td>
                            <td>{formatAmount(group.invoiceAmount)}</td><td>{formatAmount(group.advancePayment)}</td><td>{formatAmount(group.balanceAmount)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  )}
                  {hasAnyEffectiveContainer && (
                    <div className="fixation-worksheet-total">
                      <span>Total Final Amount</span>
                      <b>{formatAmount(worksheetTotal, 2)}</b>
                    </div>
                  )}
                </div>
              </section>
            </section>
          </div>
        </div>
      </div>
      <AdminDeleteApprovalDialog
        show={pendingDeleteIds.length > 0}
        scope="deal"
        dealId={dealId}
        fixationIds={pendingDeleteIds}
        onCancel={() => setPendingDeleteIds([])}
        onApproved={deleteSelectedWithAdminApproval}
      />
    </div>
  );
};

export default FixationModal;
